import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import {
    computeSceneGroupShortIds,
    resolveInsertSlotAfter,
    resolveSceneGroupHandle,
} from './scene-group-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveSceneGroupOperation } from 'js/src/luminary/luminary-operations';

export const moveSceneGroupSchema = z.object({
    scene_group_id: z
        .string()
        .describe('The stable scene_group_id of the scene group to move (from the video state).'),
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the moved group should end up immediately after. Omit to move it to the very start of the video.',
        ),
});
export type MoveSceneGroupArgs = z.infer<typeof moveSceneGroupSchema>;

export class MoveSceneGroupTool extends VideoStateTool<typeof moveSceneGroupSchema> {
    protected override readonly schema = moveSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.moveSceneGroup;
    public override readonly subagentAccess = SubagentAccess.PLACEMENT;
    protected override readonly toolDescription =
        'Reorder a scene group to a new position in the video, identified by its stable scene_group_id. The new position is set relative to another scene group: pass after_scene_group_id to place it immediately after that group, or omit after_scene_group_id to move it to the very start. Use this to fix the ordering of scene groups. Outro and brand scene groups stay pinned at the end and cannot be moved past, so a scene group cannot be moved below them.';

    public override getToolDisplayName(): string {
        return 'Moving scene group';
    }

    public get statusIcon(): string {
        return 'swap_vert';
    }

    public async execute(args: MoveSceneGroupArgs, context: ToolExecutionContext) {
        const { after_scene_group_id } = args;
        const { parentActionGroup } = context;

        // Resolve the moving group and the destination slot (fail-closed on a missing/unknown id).
        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);
        const originalIndex = sceneGroup.index;

        // resolveInsertSlotAfter gives the slot a NEW group would be inserted at (anchor index + 1,
        // or 0 for the start). moveSceneGroupToIndex first removes the moving group and then inserts
        // it at targetIndex, so when the moving group currently sits before that slot the anchor
        // shifts down by one — subtract one so "after <anchor>" lands immediately after the anchor
        // either way. This also makes the degenerate cases (anchor is the moving group itself, or the
        // group immediately before it) collapse to targetIndex === originalIndex, i.e. a no-op.
        const insertSlot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);
        const targetIndex = insertSlot - (originalIndex < insertSlot ? 1 : 0);

        const groupShortIds = computeSceneGroupShortIds(
            this.luminary.sceneGroups.map((group) => group.id),
        );
        const movedShortId = groupShortIds.get(sceneGroup.id) ?? sceneGroup.id;
        const placement =
            after_scene_group_id && after_scene_group_id.trim() !== ''
                ? `right after scene group ${after_scene_group_id}`
                : 'the start of the video';

        if (targetIndex === originalIndex) {
            return `Scene group ${movedShortId} is already at ${placement}. Nothing to move.`;
        }

        try {
            const { moveSceneGroup } = makeOperationsCallable(
                { moveSceneGroupOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            // The operation guards blocked down-moves and checkpoints itself.
            moveSceneGroup(sceneGroup.id, targetIndex, false, parentActionGroup);

            if (sceneGroup.index === originalIndex) {
                return `Error: Could not move scene group ${movedShortId} to ${placement}. It is likely blocked by an outro/brand scene group or a locked scene sequence that must stay at the end.`;
            }

            return `Success: Moved scene group ${movedShortId} to ${placement}. The video now has ${this.luminary.sceneGroups.length} scene groups.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error moving scene group: ${errorMessage}`;
        }
    }
}
